import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { clickEndsPress, isPlainMousePress } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12]
import styled, { css } from 'styled-components';
import { lgSurface, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { buildNavItems, NavItem } from '../config/sections';
import { setPageEnterSide } from '../services/pageEnterSide'; // [DOCK-MOTION 2026-09-22]
import { useIsMobile } from '../hooks/useIsMobile'; // [PHONE-LAYOUT 2026-10-22] the tab row's edge fades

/* Floating liquid-glass dock — bottom-centre, translucent so the data scrolling
   underneath reflects through. Every section is a pill (icon over label); a
   single shared indicator slides between the active pills instead of the
   highlight popping around. */
const Dock = styled.nav`
  position: fixed;
  left: 50%;
  bottom: 18px;
  transform: translateX(-50%);
  z-index: 850;
  display: flex;
  align-items: stretch;
  max-width: calc(100vw / var(--app-zoom, 1) - 20px); /* [APP-FIT 2026-10-26] the window in page px (utils/appZoom) */
  padding: 7px;
  border-radius: 999px;
  /* [DOCK-HOVER 2026-09-29, user] The glass no longer clips: a hovered tab pops up past its top edge.
     The sideways scroll moved to DockRow below. */

  /* ⚠ [PERF-4K 2026-08-14] DO NOT remove this blur. It was tried and REVERTED the same day.
   *
   * The performance case for removing it is real and still stands: backdrop-filter is the most
   * expensive thing a fixed element can do, because the browser re-captures and re-blurs what is
   * behind it on EVERY frame that anything back there moves — and this dock is the worst case in
   * the app by design, position:fixed over the scrolling table. The cost scales with pixel count,
   * so 4K quadruples it, against a 120Hz frame budget of 8.3ms.
   *
   * It was removed (alpha raised to ~0.95 to compensate, since the blur had been doing the work
   * of obscuring the rows behind) and the user rejected it on sight: "make it just like it was
   * before, the transparent one — right now the colour is bold and I am not seeing any reflection
   * from behind". The see-through reflection IS the intended look of this dock; a solid surface
   * is not an acceptable trade here whatever it buys in frames.
   *
   * So: the scroll cost of this element is ACCEPTED. If high-refresh scrolling is chased again,
   * find it somewhere other than this dock's blur. */
  background: ${(p) =>
    p.theme.colorScheme === 'dark'
      ? 'linear-gradient(180deg, rgba(44, 44, 44, 0.55) 0%, rgba(30, 30, 30, 0.62) 100%)'
      : 'linear-gradient(180deg, rgba(255, 255, 255, 0.55) 0%, rgba(230, 233, 231, 0.62) 100%)'};
  backdrop-filter: blur(40px) saturate(180%);
  -webkit-backdrop-filter: blur(40px) saturate(180%);
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.65)')};
  box-shadow:
    0 20px 50px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.22)')},
    0 4px 14px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.10)')},
    inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.65)')};

  @media (max-width: 600px) {
    bottom: 12px;
    max-width: calc(100vw - 12px);
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  /* [LIQUID-GLASS 2026-11-02, user] (preview approved: https://claude.ai/artifact/EfDUMoqsSytqBx3BKVrHdR) the app's glass recipe:
     a see-through tint with a fine grain and a shine, a bright edge on top, a soft reflection at the bottom, a softer blur and
     colour boost (was blur 40 px, 180 %); the border stays 1 px wide but clear, so the dock keeps its exact size */
  border-color: transparent;
  ${lgSurface(34)}
`;

/* [DOCK-HOVER 2026-09-29, user] Hover pop: with a mouse, the tab under the cursor grows HOVER_MAG from
   its bottom edge and rises HOVER_LIFT px per unit of growth (1.3x → 1.8px). Only that tab moves: the
   rest of the row stays still (user: "the tab will only pop up and not move the full row" — the
   macOS-style wave, where neighbours grew and the row made room, was tried and dropped). The lozenge
   under an active tab pops with it (PillMotion.frame uses the same numbers). */
const HOVER_MAG = 1.3;
/** The active tab pops less: its green lozenge grows with it, and at 1.3 a wide one (Performance
 *  Review, ~140px) reached ~3px under its neighbours' labels. At 1.15 a lozenge clears them on any tab
 *  up to ~180px wide. A clicked (popped) tab settles to this as it becomes active. */
const HOVER_MAG_ACTIVE = 1.15;
const HOVER_LIFT = 6;
const hoverLift = (mag: number) => ((mag - 1) * HOVER_LIFT).toFixed(2);
/** The lozenge's spring (critically damped, ω = LEAD_OMEGA) sampled over 420 ms — the same curve as
 *  the active icon's lift — so a popped tab and its lozenge move together. */
const SPRING_EASE = 'linear(0, 0.044, 0.143, 0.263, 0.383, 0.494, 0.592, 0.675, 0.743, 0.799, 0.844, 0.879, 0.907, 0.929, 0.946, 0.959, 0.969, 0.976, 0.982, 0.987, 1)';

/* [DOCK-HOVER 2026-09-29, user] The tab row. It scrolls sideways when the tabs do not fit (it used to
   be the Dock itself), and it reaches HOVER_ROOM px above the glass so a popped tab has room to rise:
   overflow-x: auto forces overflow-y to clip, so the room has to be inside the scroller. The strip is
   transparent and lets clicks through to the page (pointer-events: none); only the tabs catch them. */
const HOVER_ROOM = 20;
const DockRow = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
  padding-top: ${HOVER_ROOM}px;
  margin-top: -${HOVER_ROOM}px;
  pointer-events: none;
  & > button { pointer-events: auto; }

  /* [PHONE-LAYOUT 2026-10-22] Phones: the tabs that run past an end of the row fade out there (only on the side
     that has more tabs, from data-fade set in BottomNav), so a half-shown label reads as "swipe for more" rather
     than as a cut word. The glass behind shows through the fade. */
  @media (max-width: 768px) {
    &[data-fade='right'] {
      -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 30px), transparent);
      mask-image: linear-gradient(to right, #000 calc(100% - 30px), transparent);
    }
    &[data-fade='left'] {
      -webkit-mask-image: linear-gradient(to left, #000 calc(100% - 30px), transparent);
      mask-image: linear-gradient(to left, #000 calc(100% - 30px), transparent);
    }
    &[data-fade='both'] {
      -webkit-mask-image: linear-gradient(to right, transparent, #000 30px, #000 calc(100% - 30px), transparent);
      mask-image: linear-gradient(to right, transparent, #000 30px, #000 calc(100% - 30px), transparent);
    }
  }
`;

/* [DOCK-MOTION 2026-09-22] The sliding green lozenge, rebuilt so it glides at full frame rate
   even while the newly opened tab is still being built.

   WHY: it used to slide by transitioning left/width/top/height. Those are layout properties, so
   every frame of the slide needed the main thread, the same thread that is busy building the new
   page. Measured on the live site (22 Sep, 10 switches): 4 slides hitched for 33-83 ms (Inventory,
   PM Performance, Stats, Payments), and the slide only began once the new page had been built
   (PM Performance: a 72 ms block at the click).

   HOW: the lozenge is two round caps and a middle strip, plus a soft glow that stands in for the
   old drop shadow. They move ONLY with transform. The strip is stretched with scaleX, which is
   safe because it has no rounded corners; the caps never scale, so the ends stay perfectly round
   at every width. The path comes from a critically damped spring (no overshoot), sampled into
   Web Animations keyframes, which the browser plays on the compositor, so the lozenge keeps
   gliding while the main thread is busy. */
const PillLayer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  z-index: 0;
  pointer-events: none;

  & > span {
    position: absolute;
    left: 0;
    top: 0;
    display: block;
    transform-origin: 0 0;
  }
`;

const PillCap = styled.span<{ $side: 'left' | 'right' }>`
  background: linear-gradient(180deg, ${(p) => p.theme.green600} 0%, ${(p) => p.theme.green700} 100%);
  border-radius: ${(p) => (p.$side === 'left' ? '999px 0 0 999px' : '0 999px 999px 0')};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.30);
  /* [LIQUID-GLASS 2026-11-02, user] */
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.34), rgba(255, 255, 255, 0) 52%), linear-gradient(180deg, ${(p) => p.theme.green600} 0%, ${(p) => p.theme.green700} 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55), inset 0 -1px 0 rgba(0, 0, 0, 0.16); /* glossy: a shine and a light top edge */
`;

/* 100px wide, scaled to fit (see MID_BASE). */
const PillMid = styled.span`
  width: 100px;
  background: linear-gradient(180deg, ${(p) => p.theme.green600} 0%, ${(p) => p.theme.green700} 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.30);
  /* [LIQUID-GLASS 2026-11-02, user] */
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.34), rgba(255, 255, 255, 0) 52%), linear-gradient(180deg, ${(p) => p.theme.green600} 0%, ${(p) => p.theme.green700} 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55), inset 0 -1px 0 rgba(0, 0, 0, 0.16); /* glossy: a shine and a light top edge */
`;

/* The old indicator's "0 6px 16px rgba(20,107,84,0.40)" drop shadow, as its own layer: a
   box-shadow on the stretched strip would stretch with it. A 16px shadow blur equals an 8px
   filter blur, and the blur hides the horizontal scaling. */
const PillGlow = styled.span`
  width: 100px;
  border-radius: 999px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(11,102,64,0.40)' : 'rgba(20, 107, 84, 0.40)')};
  filter: blur(8px);
  /* [LIQUID-GLASS 2026-11-02, user] */
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.35)' : 'rgba(16,185,129,0.38)')}; /* the glossy buttons' soft green glow */
`;

const Item = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 62px;
  padding: 7px 14px;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  background: transparent;
  color: ${(p) => (p.$active ? '#fff' : p.theme.muted)};
  /* [DOCK-MOTION 2026-09-22] The tab being left drops its white label in 140ms, because the
     lozenge moves off it at once and white text on the light glass would vanish; the tab being
     reached takes 220ms, about when the lozenge arrives under it. */
  transition: color ${(p) => (p.$active ? '220ms' : '140ms')} cubic-bezier(0.4, 0, 0.2, 1), transform 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${(p) => (p.$active ? '#fff' : p.theme.colorScheme === 'dark' ? '#fff' : p.theme.text)};
  }
  /* [DOCK-MOTION 2026-09-22] Press = a slight give (compositor-only), was a 1px drop. */
  &:active { transform: scale(0.96); }

  /* [DOCK-HOVER 2026-09-29, user] Mouse only (a touch screen's sticky :hover would leave a tab popped
     after a tap). The tab under the cursor pops (see HOVER_MAG) on the lozenge's spring and draws over
     its neighbours; nothing else moves. Its icon turns green — the only colour change (the spotlight's
     green glow was tried and dropped by the user). Not on the active tab's icon, which sits on green. */
  @media (hover: hover) and (pointer: fine) {
    transform-origin: 50% 100%;
    transition:
      color ${(p) => (p.$active ? '220ms' : '140ms')} cubic-bezier(0.4, 0, 0.2, 1),
      transform 420ms cubic-bezier(0.22, 1, 0.36, 1);
    &:hover {
      z-index: 2;
      transform: ${(p) => {
        const mag = p.$active ? HOVER_MAG_ACTIVE : HOVER_MAG;
        return `translateY(-${hoverLift(mag)}px) scale(${mag})`;
      }};
    }
    &:hover:active {
      transform: ${(p) => {
        const mag = p.$active ? HOVER_MAG_ACTIVE : HOVER_MAG;
        return `translateY(-${hoverLift(mag)}px) scale(${(mag * 0.96).toFixed(3)})`;
      }};
    }
    ${(p) => !p.$active && css`
      &:hover i { color: ${p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600}; }
    `}
  }
  @supports (transition-timing-function: linear(0, 1)) {
    @media (hover: hover) and (pointer: fine) {
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1), ${SPRING_EASE};
    }
  }
  @media (hover: hover) and (pointer: fine) and (prefers-reduced-motion: reduce) {
    &:hover, &:hover:active { transform: none; }
  }

  i {
    /* inline-block so the transform below applies (a plain inline box ignores transforms) */
    display: inline-block;
    font-size: 16px;
    line-height: 1;
    /* [DOCK-MOTION 2026-09-22] The active icon lifts 1px and grows 6% on the same spring as the
       lozenge; a plain ease-out is the fallback for browsers without linear(). */
    transform: ${(p) => (p.$active ? 'translateY(-1px) scale(1.06)' : 'none')};
    transition: transform 420ms cubic-bezier(0.22, 1, 0.36, 1), color 140ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  @supports (transition-timing-function: linear(0, 1)) {
    i {
      transition-timing-function: linear(0, 0.044, 0.143, 0.263, 0.383, 0.494, 0.592, 0.675, 0.743, 0.799, 0.844, 0.879, 0.907, 0.929, 0.946, 0.959, 0.969, 0.976, 0.982, 0.987, 1);
    }
  }
  span {
    /* [DOCK-MOTION 2026-09-22] Column flex + the hidden bold copy below keep every tab at its BOLD
       width all the time. The label still turns bold when active, but no tab widens any more.
       Before, 18 of the 20 tabs shifted 1-2 px sideways on every switch. */
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    font-size: 11px;
    font-weight: ${(p) => (p.$active ? 700 : 500)};
    white-space: nowrap;
    letter-spacing: 0.2px;

    &::after {
      content: attr(data-text);
      font-weight: 700;
      height: 0;
      visibility: hidden;
      overflow: hidden;
    }
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  /* a soft light where the tab is pressed (startLiquidPress); the tab already squishes */
  overflow: hidden;
  isolation: isolate;
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(70px circle at var(--lg-x, 50%) var(--lg-y, 50%), ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.30)' : 'rgba(255,255,255,0.85)')}, transparent 65%);
    opacity: 0;
    transition: opacity 700ms ease;
    pointer-events: none;
  }
  &[data-lg-lit]::after { opacity: 1; transition: opacity 0s; }
`;

type Box = { left: number; top: number; width: number; height: number };

/* ── [DOCK-MOTION 2026-09-22] spring motion ──────────────────────────────────────────────────── */

/** One edge of the lozenge: a critically damped spring toward `target` (px). `w` sets the speed:
 *  it settles in about 6.64 / w seconds and never overshoots. */
type Spring = { target: number; d0: number; v0: number; w: number };

function springAt(s: Spring, t: number): { x: number; v: number } {
  const e = Math.exp(-s.w * t);
  const c = s.v0 + s.w * s.d0;
  return { x: s.target + (s.d0 + c * t) * e, v: e * (s.v0 - s.w * t * c) };
}

/** `eps` / `veps` = "settled" distance and speed: px for an edge; a unitless amount for the swell. */
function settleTime(s: Spring, eps = 0.25, veps = 6): number {
  for (let t = 0; t < 1.6; t += 0.004) {
    const p = springAt(s, t);
    if (Math.abs(p.x - s.target) < eps && Math.abs(p.v) < veps) return Math.max(t, 0.05);
  }
  return 1.6;
}

/* The edge moving toward the new tab settles in ~0.42 s and the trailing edge in ~0.46 s, so the
   lozenge stretches a little toward its target and gathers in behind it. That is the soft,
   continuous feel of the iPhone tab bar, with the bounce left out. The stretch peaks at 5% of the
   distance travelled (a neighbouring tab: ~5-8 px; the whole dock: ~50 px). A slower trailing edge
   (13 = 11%, over 100 px on a long jump) looked rubbery, not professional. */
const LEAD_OMEGA = 16;
const TRAIL_OMEGA = 14.5;
/** Width of PillMid / PillGlow before scaling. */
const MID_BASE = 100;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

type PillParts = { glow: HTMLElement; capL: HTMLElement; mid: HTMLElement; capR: HTMLElement };

class PillMotion {
  private L: Spring | null = null;
  private R: Spring | null = null;
  // [DOCK-HOVER 2026-09-29] the hover pop of the tab under the lozenge (1 = rest size), on its own spring
  private S: Spring | null = null;
  private t0 = 0;
  private anims: Animation[] = [];
  private y = 0;
  private h = 0;
  private target: { x: number; w: number; s: number } | null = null;

  constructor(private parts: PillParts) {}

  isMoving(): boolean {
    return this.anims.some((a) => a.playState === 'running');
  }

  private geom(y: number, h: number): void {
    this.y = y;
    if (h === this.h) return;
    this.h = h;
    const { glow, capL, mid, capR } = this.parts;
    for (const el of [glow, capL, mid, capR]) el.style.height = `${h}px`;
    capL.style.width = `${h / 2}px`;
    capR.style.width = `${h / 2}px`;
  }

  /* [DOCK-HOVER 2026-09-29, user] `s` = the hover pop of the tab under the lozenge. The rest-size
     lozenge is scaled by s about its bottom centre and lifted like the tab (HOVER_LIFT), so it stays
     exactly under the popped tab; the caps scale evenly, so their ends stay round. s = 1 gives the
     plain lozenge (the same transforms as before, written with scale(a, b) so every keyframe has the
     same function list). */
  private frame(xl: number, xr: number, s = 1): Record<keyof PillParts, string> {
    const r = this.h / 2;
    const y = this.y;
    const px = (xl + xr) / 2;
    const py = y + this.h;
    const lift = (s - 1) * HOVER_LIFT;
    const X = (x: number) => px + s * (x - px);
    const Y = (v: number) => py + s * (v - py) - lift;
    const sm = Math.max(0, (xr - xl - 2 * r + 1) / MID_BASE);
    const sg = Math.max(0, (xr - xl) / MID_BASE);
    return {
      capL: `translate(${X(xl)}px, ${Y(y)}px) scale(${s}, ${s})`,
      capR: `translate(${X(xr - r)}px, ${Y(y)}px) scale(${s}, ${s})`,
      // The strip overlaps each cap by half a pixel so no seam can open between them.
      mid: `translate(${X(xl + r - 0.5)}px, ${Y(y)}px) scale(${s * sm}, ${s})`,
      glow: `translate(${X(xl)}px, ${Y(y + 6)}px) scale(${s * sg}, ${s})`,
    };
  }

  private apply(f: Record<keyof PillParts, string>): void {
    (Object.keys(f) as (keyof PillParts)[]).forEach((k) => { this.parts[k].style.transform = f[k]; });
  }

  private cancel(): void {
    this.anims.forEach((a) => a.cancel());
    this.anims = [];
  }

  /** Put the lozenge on a tab with no motion (first placement, a resize while idle). `s` = that
   *  tab's hover pop ([DOCK-HOVER]; 1 when it is not hovered). */
  jump(b: Box, s = 1): void {
    this.cancel();
    this.geom(b.top, b.height);
    this.L = { target: b.left, d0: 0, v0: 0, w: 1 };
    this.R = { target: b.left + b.width, d0: 0, v0: 0, w: 1 };
    this.S = { target: s, d0: 0, v0: 0, w: 1 };
    this.t0 = performance.now();
    this.target = { x: b.left, w: b.width, s };
    this.apply(this.frame(b.left, b.left + b.width, s));
  }

  /** Glide to a tab. Called again mid-glide, each edge keeps its current speed, so the path bends
   *  toward the new tab instead of restarting from a standstill. [DOCK-HOVER] The pop glides on its
   *  own spring the same way, so a click on a popped tab lands the lozenge popped with it. */
  glideTo(b: Box, s = 1): void {
    if (!this.L || !this.R || !this.S || prefersReducedMotion() || typeof this.parts.mid.animate !== 'function') {
      this.jump(b, s);
      return;
    }
    if (this.target && Math.abs(this.target.x - b.left) < 0.5 && Math.abs(this.target.w - b.width) < 0.5
      && Math.abs(this.target.s - s) < 0.002) return;
    const t = (performance.now() - this.t0) / 1000;
    const cl = springAt(this.L, t);
    const cr = springAt(this.R, t);
    const cs = springAt(this.S, t);
    const right = b.left > cl.x;
    this.geom(b.top, b.height);
    this.L = { target: b.left, d0: cl.x - b.left, v0: cl.v, w: right ? TRAIL_OMEGA : LEAD_OMEGA };
    this.R = { target: b.left + b.width, d0: cr.x - (b.left + b.width), v0: cr.v, w: right ? LEAD_OMEGA : TRAIL_OMEGA };
    this.S = { target: s, d0: cs.x - s, v0: cs.v, w: LEAD_OMEGA };
    this.t0 = performance.now();
    this.target = { x: b.left, w: b.width, s };

    const T = Math.max(settleTime(this.L), settleTime(this.R), settleTime(this.S, 0.002, 0.05));
    const n = Math.max(2, Math.ceil(T * 60) + 1); // one keyframe per 60 Hz frame; the compositor interpolates between them
    const frames: Record<keyof PillParts, Keyframe[]> = { glow: [], capL: [], mid: [], capR: [] };
    for (let i = 0; i < n; i++) {
      const tt = (T * i) / (n - 1);
      const f = this.frame(springAt(this.L, tt).x, springAt(this.R, tt).x, springAt(this.S, tt).x);
      (Object.keys(frames) as (keyof PillParts)[]).forEach((k) => frames[k].push({ transform: f[k] }));
    }
    this.cancel();
    this.apply(this.frame(b.left, b.left + b.width, s)); // the resting place once the keyframes end
    (Object.keys(frames) as (keyof PillParts)[]).forEach((k) => {
      this.anims.push(this.parts[k].animate(frames[k], { duration: T * 1000, easing: 'linear' }));
    });
  }
}

/** [DOCK-HOVER 2026-09-29] Does this pointer + motion setting get the hover pop (mouse, not reduced motion)? */
const hoverPops = (): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches
  && !prefersReducedMotion();

const isItemActive = (item: NavItem, pathname: string): boolean =>
  item.matchPaths.some((p) => pathname === p || pathname.startsWith(p + '/'));

const BottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const dockRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile(); // [PHONE-LAYOUT 2026-10-22] the tab row's edge fades
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());
  const glowRef = useRef<HTMLSpanElement>(null);
  const capLRef = useRef<HTMLSpanElement>(null);
  const midRef = useRef<HTMLSpanElement>(null);
  const capRRef = useRef<HTMLSpanElement>(null);
  const motionRef = useRef<PillMotion | null>(null);
  // Skip the glide on the very first placement so the lozenge appears under the active tab
  // instead of flying in from the left.
  const placedRef = useRef(false);

  // Same source of truth as the routing: admins see every section plus User
  // Management; everyone else sees only their granted sections. Refunds +
  // Disputes render as one merged pill (see buildNavItems).
  const isAdmin = !!user?.is_admin;
  const allowed = new Set(user?.allowed_sections || []);
  const items = buildNavItems(isAdmin, allowed);
  // [DOCK-MOTION 2026-09-22] The clicked tab lights up on the click itself; the route follows a
  // frame later (see onSelect). Cleared as soon as the route has changed.
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const shownPath = pendingPath ?? location.pathname;
  // [FLUID-8 2026-10-12, user] A tab switches on the mouse PRESS, as browser tabs do (the click only comes on release,
  // ~0.1 s in). Touch keeps the tap (a press on the scrolling dock may be a swipe); the keyboard's Enter / Space click.
  const dockPressedRef = useRef(false);
  const activeIndex = items.findIndex((item) => isItemActive(item, shownPath));
  const activeId = activeIndex >= 0 ? items[activeIndex].id : null;

  useEffect(() => {
    setPendingPath(null);
  }, [location.pathname]);

  const motion = (): PillMotion | null => {
    if (!motionRef.current && glowRef.current && capLRef.current && midRef.current && capRRef.current) {
      motionRef.current = new PillMotion({ glow: glowRef.current, capL: capLRef.current, mid: midRef.current, capR: capRRef.current });
    }
    return motionRef.current;
  };

  const boxOf = (btn: HTMLButtonElement): Box =>
    ({ left: btn.offsetLeft, top: btn.offsetTop, width: btn.offsetWidth, height: btn.offsetHeight });

  /** `popped` = the tab is under the mouse ([DOCK-HOVER]): the lozenge pops with it. Worked out from
   *  :hover unless the caller knows better (a pointerleave handler, where :hover may not have cleared). */
  const place = (btn: HTMLButtonElement | undefined, glide: boolean, popped?: boolean) => {
    const m = motion();
    if (!m || !btn) return;
    // the lozenge is always under the ACTIVE tab, so it pops by the active tab's amount
    const s = (popped ?? btn.matches(':hover')) && hoverPops() ? HOVER_MAG_ACTIVE : 1;
    if (glide && placedRef.current) m.glideTo(boxOf(btn), s);
    else m.jump(boxOf(btn), s);
    placedRef.current = true;
  };

  // Follow the active tab: a dock click (pendingPath), Back/Forward, a link from inside a page.
  // Runs before paint, so the glide starts in the same frame as the click. When the route then
  // lands on the tab the lozenge is already heading for, glideTo() leaves it alone.
  useLayoutEffect(() => {
    const btn = activeId ? itemRefs.current.get(activeId) : undefined;
    const first = !placedRef.current;
    place(btn, true);
    btn?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: first ? 'auto' : 'smooth' });
  }, [activeId, items.length]);

  // Keep the lozenge on its tab through any reflow: the FontAwesome glyphs and label fonts are not
  // loaded at the first measurement on a hard refresh, and the window can resize. Mid-glide it
  // re-targets smoothly; at rest it simply re-pins.
  useEffect(() => {
    const repin = () => {
      const btn = activeId ? itemRefs.current.get(activeId) : undefined;
      place(btn, !!motionRef.current?.isMoving());
    };
    window.addEventListener('resize', repin);
    const fonts = (document as any).fonts;
    if (fonts?.ready) fonts.ready.then(repin).catch(() => {});

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(repin);
      const btn = activeId ? itemRefs.current.get(activeId) : undefined;
      if (btn) ro.observe(btn);
      if (dockRef.current) ro.observe(dockRef.current);
    }
    return () => {
      window.removeEventListener('resize', repin);
      ro?.disconnect();
    };
  }, [activeId, items.length]);

  // [PHONE-LAYOUT 2026-10-22] Phones only: mark which end(s) of the tab row have more tabs past them (data-fade =
  // left / right / both), for the edge fades in DockRow's phone CSS. Re-read on every scroll, resize and font load.
  // Never set on a laptop (the attribute is removed there), so the laptop dock is untouched. The row is the dock's
  // first child (DockRow), reached through dockRef so the dock's markup stays exactly as it was.
  useEffect(() => {
    const row = dockRef.current?.firstElementChild as HTMLElement | null | undefined;
    if (!row) return undefined;
    if (!isMobile) { row.removeAttribute('data-fade'); return undefined; }
    const update = () => {
      const max = row.scrollWidth - row.clientWidth;
      const left = row.scrollLeft > 2;
      const right = row.scrollLeft < max - 2;
      const v = left && right ? 'both' : left ? 'left' : right ? 'right' : '';
      if (v) row.setAttribute('data-fade', v); else row.removeAttribute('data-fade');
    };
    update();
    row.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const fonts = (document as any).fonts;
    if (fonts?.ready) fonts.ready.then(update).catch(() => {});
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(update); ro.observe(row); }
    return () => {
      row.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [isMobile, items.length]);

  const onSelect = (item: NavItem, index: number) => {
    if (index !== activeIndex) {
      // The tab lights up and the lozenge starts on the click itself (the layout effect above)...
      setPendingPath(item.path);
      if (activeIndex >= 0) setPageEnterSide(index > activeIndex ? 1 : -1);
    }
    // ...and the route change waits one frame, so the glide is already running on the compositor
    // before the new page starts building (about 16 ms; nothing a person can notice).
    requestAnimationFrame(() => setTimeout(() => navigate(item.path), 0));
  };

  if (items.length === 0) return null;

  return (
    <Dock ref={dockRef} aria-label="Sections">
      {/* [DOCK-HOVER 2026-09-29] The lozenge + tabs sit in DockRow (the scroller with room above). */}
      <DockRow>
        <PillLayer aria-hidden="true">
          <PillGlow ref={glowRef} />
          <PillCap ref={capLRef} $side="left" />
          <PillMid ref={midRef} />
          <PillCap ref={capRRef} $side="right" />
        </PillLayer>
        {items.map((item, index) => {
          const isActive = index === activeIndex;
          return (
            <Item
              key={item.id}
              ref={(el) => {
                if (el) itemRefs.current.set(item.id, el);
                else itemRefs.current.delete(item.id);
              }}
              $active={isActive}
              aria-current={isActive ? 'page' : undefined}
              aria-label={item.text}
              onPointerDown={(e) => {
                dockPressedRef.current = isPlainMousePress(e);
                if (dockPressedRef.current) onSelect(item, index);
              }}
              onClick={(e) => {
                if (clickEndsPress(dockPressedRef, e)) return; // the press already switched
                onSelect(item, index);
              }}
              // [DOCK-HOVER 2026-09-29] The active tab's lozenge pops and settles with the tab (CSS pops the tab itself).
              onPointerEnter={(e) => { if (isActive && e.pointerType === 'mouse') place(e.currentTarget, true, true); }}
              onPointerLeave={(e) => { if (isActive && e.pointerType === 'mouse') place(e.currentTarget, true, false); }}
            >
              <i className={item.icon} />
              <span data-text={item.text}>{item.text}</span>
            </Item>
          );
        })}
      </DockRow>
    </Dock>
  );
};

export default BottomNav;
